/**
 * HeatmapTab — Self-contained Leaflet heatmap for SCMejora incidents.
 * Supports two view modes: heatmap (heat layer) and clusters (MarkerClusterGroup).
 * Manages its own data fetching with tipo + date filters.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Loader2, X, Flame, Layers } from 'lucide-react';
import FilterCombobox from './FilterCombobox';
import { tasksAPI } from '../lib/api';
import api from '../lib/api';

const PRIORITY_COLOR = (w) => w >= 5 ? '#ef4444' : w >= 4 ? '#f97316' : w >= 3 ? '#f59e0b' : '#3b82f6';

export default function HeatmapTab({ appliedFrom, appliedTo }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const heatRef = useRef(null);
  const clusterRef = useRef(null);
  const [selectedTipo, setSelectedTipo] = useState('');
  const [viewMode, setViewMode] = useState('heatmap'); // 'heatmap' | 'clusters'

  // Fetch tipo options
  const { data: optData } = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => tasksAPI.getFilterOptions().then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });
  const tipos = optData?.tipos || [];

  // Build query params
  const qparams = new URLSearchParams();
  if (appliedFrom) qparams.set('date_from', appliedFrom);
  if (appliedTo) qparams.set('date_to', appliedTo);
  if (selectedTipo) qparams.set('tipo', selectedTipo);
  const qstr = qparams.toString() ? `?${qparams.toString()}` : '';

  // Fetch heatmap data
  const { data: heatmapData, isLoading, error } = useQuery({
    queryKey: ['analytics-heatmap-detail', appliedFrom, appliedTo, selectedTipo],
    queryFn: () => api.get(`/analytics/heatmap${qstr}`).then(r => r.data),
    staleTime: 3 * 60 * 1000,
  });
  const points = heatmapData?.points || [];

  // Initialize map once on mount
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    let map;

    Promise.all([
      import('leaflet'),
      import('leaflet/dist/leaflet.css'),
    ]).then(([L]) => {
      const leaflet = L.default || L;
      delete leaflet.Icon.Default.prototype._getIconUrl;
      leaflet.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
      map = leaflet.map(mapContainerRef.current, { center: [28.4636, -16.2518], zoom: 12 });
      leaflet.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
    }).catch(err => console.error('Leaflet init error:', err));

    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; heatRef.current = null; clusterRef.current = null; }
    };
  }, []); // only on mount/unmount

  // Update display layer when points or viewMode change
  useEffect(() => {
    if (!mapRef.current || !points.length) return;
    let cancelled = false;

    (async () => {
      const L = await import('leaflet');
      const leaflet = L.default || L;
      const map = mapRef.current;
      if (cancelled || !map) return;

      // Clean up both layers
      if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }
      if (clusterRef.current) { map.removeLayer(clusterRef.current); clusterRef.current = null; }

      if (viewMode === 'heatmap') {
        await import('leaflet.heat');
        if (cancelled || !mapRef.current) return;
        const heatData = points.map(p => [p.lat, p.lng, p.weight / 5]);
        const heat = leaflet.heatLayer(heatData, {
          radius: 18, blur: 20, maxZoom: 17, max: 1.0,
          gradient: { 0.2: '#3b82f6', 0.5: '#f59e0b', 0.8: '#ef4444' },
        });
        heat.addTo(map);
        heatRef.current = heat;
      } else {
        // Cluster mode — import both CSS and plugin
        await Promise.all([
          import('leaflet.markercluster/dist/MarkerCluster.css'),
          import('leaflet.markercluster/dist/MarkerCluster.Default.css'),
          import('leaflet.markercluster'),
        ]);
        if (cancelled || !mapRef.current) return;
        const mcg = leaflet.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 60 });
        points.forEach(p => {
          leaflet.circleMarker([p.lat, p.lng], {
            radius: 5,
            fillColor: PRIORITY_COLOR(p.weight),
            color: 'white',
            weight: 1,
            fillOpacity: 0.85,
          }).bindTooltip(`Prioridad ${p.weight}`, { permanent: false }).addTo(mcg);
        });
        mcg.addTo(map);
        clusterRef.current = mcg;
      }

      // Fit bounds to visible points
      const lats = points.map(p => p.lat);
      const lngs = points.map(p => p.lng);
      map.fitBounds(
        [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
        { padding: [40, 40] }
      );
    })();

    return () => { cancelled = true; };
  }, [points, viewMode]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
        <MapPin className="w-8 h-8 opacity-30" />
        <p className="text-sm">Error al cargar el mapa</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Tipo filter */}
        <div className="flex-1 min-w-[200px] max-w-xs">
          <FilterCombobox
            label="tipo-heatmap"
            value={selectedTipo}
            options={tipos}
            onChange={setSelectedTipo}
            placeholder="Filtrar por tipo de incidencia..."
            width="320px"
          />
        </div>
        {selectedTipo && (
          <button
            onClick={() => setSelectedTipo('')}
            data-testid="heatmap-clear-tipo"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-3.5 h-3.5" /> Todos los tipos
          </button>
        )}
        {isLoading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}

        {/* View mode toggle */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden ml-auto">
          <button
            onClick={() => setViewMode('heatmap')}
            data-testid="heatmap-mode-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'heatmap' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:text-foreground'}`}
          >
            <Flame className="w-3.5 h-3.5" /> Calor
          </button>
          <button
            onClick={() => setViewMode('clusters')}
            data-testid="clusters-mode-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'clusters' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:text-foreground'}`}
          >
            <Layers className="w-3.5 h-3.5" /> Clusters
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 text-primary" />
          <span className="font-medium">
            {isLoading ? 'Cargando...' : `${points.length.toLocaleString('es-ES')} incidencias`}
            {selectedTipo ? ` — ${selectedTipo.length > 30 ? selectedTipo.substring(0, 30) + '…' : selectedTipo}` : ''}
          </span>
        </div>
        {viewMode === 'heatmap' ? (
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-500 opacity-80" /> Baja</div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-yellow-500 opacity-80" /> Media</div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500 opacity-80" /> Alta densidad</div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>Los círculos muestran el número de incidencias agrupadas. Haz zoom para ver individuales.</span>
          </div>
        )}
      </div>

      {/* Map container */}
      <div
        ref={mapContainerRef}
        data-testid="heatmap-container"
        className="rounded-xl overflow-hidden border border-border"
        style={{ height: '420px', width: '100%', zIndex: 0 }}
      />
    </div>
  );
}
